import { IMAGE_UPLOAD_LIMIT } from '@feedback-service/shared';
import { useState, useCallback, useRef, useEffect, type ChangeEvent, type DragEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

export interface ImageUploadProps {
  label: string;
  value: File[];
  onChange: (files: File[]) => void;
  error?: string;
  disabled?: boolean;
  className?: string;
}

interface ImagePreview {
  file: File;
  url: string;
}

/**
 * 图片上传组件
 * 支持拖拽上传、点击选择、图片预览、删除和验证
 */
export function ImageUpload({
  label,
  value,
  onChange,
  error,
  disabled = false,
  className,
}: ImageUploadProps) {
  const { t } = useTranslation();
  const [previews, setPreviews] = useState<ImagePreview[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const validateFile = useCallback((file: File): string | null => {
    if (file.size > IMAGE_UPLOAD_LIMIT.maxSize) {
      return t('form.images.errorSize', {
        maxSize: '5MB',
        fileName: file.name,
      });
    }
    if (!(IMAGE_UPLOAD_LIMIT.allowedTypes as readonly string[]).includes(file.type)) {
      return t('form.images.errorType', { fileName: file.name });
    }
    return null;
  }, [t]);

  useEffect(() => {
    const valueFiles = new Set(value);
    
    const removedPreviews = previews.filter(p => !valueFiles.has(p.file));
    removedPreviews.forEach(p => URL.revokeObjectURL(p.url));
    
    const newPreviews = value
      .filter(file => !previews.some(p => p.file === file))
      .map(file => ({
        file,
        url: URL.createObjectURL(file),
      }));

    const keptPreviews = previews.filter(p => valueFiles.has(p.file));
    setPreviews([...keptPreviews, ...newPreviews]);
  }, [value]);

  useEffect(() => {
    return () => {
      previews.forEach(p => URL.revokeObjectURL(p.url));
    };
  }, [previews]);

  const handleFiles = useCallback(
    (files: FileList | null) => {
      if (!files || files.length === 0) return;

      setUploadError(null);

      const currentCount = value.length;
      const remainingSlots = IMAGE_UPLOAD_LIMIT.maxCount - currentCount;

      if (remainingSlots <= 0) {
        setUploadError(
          t('form.images.errorMax', { maxCount: IMAGE_UPLOAD_LIMIT.maxCount })
        );
        return;
      }

      const filesToAdd: File[] = [];
      const newPreviews: ImagePreview[] = [];

      const fileArray = Array.from(files).slice(0, remainingSlots);

      for (const file of fileArray) {
        const validationError = validateFile(file);
        if (validationError) {
          setUploadError(validationError);
          continue;
        }
        filesToAdd.push(file);
        newPreviews.push({
          file,
          url: URL.createObjectURL(file),
        });
      }

      if (filesToAdd.length > 0) {
        onChange([...value, ...filesToAdd]);
        setPreviews([...previews, ...newPreviews]);
      }
    },
    [value, onChange, validateFile, t, previews]
  );

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    handleFiles(e.target.files);
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  const handleDragEnter = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (!disabled) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleRemoveImage = (index: number) => {
    const newFiles = [...value];
    const newPreviews = [...previews];

    URL.revokeObjectURL(newPreviews[index].url);

    newFiles.splice(index, 1);
    newPreviews.splice(index, 1);

    onChange(newFiles);
    setPreviews(newPreviews);
  };

  const handleClick = () => {
    if (!disabled && inputRef.current) {
      inputRef.current.click();
    }
  };

  const displayError = error || uploadError;

  return (
    <div className={`space-y-2 ${className || ''}`}>
      <Label className="text-sm font-medium">{label}</Label>

      <div
        className={cn(
          'border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors',
          isDragging && 'border-primary bg-primary/5',
          displayError && 'border-destructive',
          disabled && 'opacity-50 cursor-not-allowed',
          !displayError && !isDragging && 'border-muted-foreground/25 hover:border-primary/50'
        )}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={handleClick}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-label={t('form.images.dropzone')}
      >
        <input
          ref={inputRef}
          type="file"
          accept={IMAGE_UPLOAD_LIMIT.allowedTypes.join(',')}
          multiple
          onChange={handleInputChange}
          disabled={disabled}
          className="hidden"
          aria-hidden="true"
        />

        <div className="space-y-2">
          <p className="text-sm text-muted-foreground">
            {t('form.images.dropzone')}
          </p>
          <p className="text-xs text-muted-foreground">
            {t('form.images.hint', {
              maxCount: IMAGE_UPLOAD_LIMIT.maxCount,
              maxSize: '5MB',
            })}
          </p>
        </div>
      </div>

      {previews.length > 0 && (
        <div className="grid grid-cols-3 gap-2 mt-2">
          {previews.map((preview, index) => (
            <div key={preview.url} className="relative group">
              <img
                src={preview.url}
                alt={`${t('form.images.preview')} ${index + 1}`}
                className="w-full h-24 object-cover rounded-lg border"
              />
              <Button
                type="button"
                variant="destructive"
                size="sm"
                className="absolute top-1 right-1 h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemoveImage(index);
                }}
                disabled={disabled}
                aria-label={t('form.images.remove')}
              >
                ×
              </Button>
            </div>
          ))}
        </div>
      )}

      {displayError && (
        <p className="text-sm text-destructive" role="alert">
          {displayError}
        </p>
      )}
    </div>
  );
}
